import { useCallback, useEffect, useMemo, useState, useRef, ComponentProps } from 'react';
import type React from 'react';
import {
  Box,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  Typography,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  ButtonGroup,
  TablePaginationProps,
  alpha,
  useTheme,
  styled,
  Fade,
  Grow,
  InputAdornment,
  Chip as MuiChip,
  IconButton,
  Collapse,
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search,
  Filter,
  X,
  Download,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Building2,
  Users,
  Tag,
  Settings,
} from 'lucide-react';

import useUrl from 'hooks/useUrl';
import { useTablePagination } from 'hooks/useTablePagination';
import { useSearchTeamsByFieldsQuery } from 'TeamModule/query/hooks/useSearchTeamsByFieldsQuery';
import { SearchTeamsByFieldsPayload, TeamSearchItem } from 'TeamModule/api/searchTeamsByFieldsApi';
import { arrayToCsv, booleanToString } from 'utils/query';
import TeamsV2Table from 'TeamModule/components/TeamsV2Table/TeamsV2Table';
import TeamTagsAutocomplete from 'components/TeamTagsAutocomplete';
import { getTeamStatus } from 'TeamModule/components/TeamsV2Table/DataCells/TeamsV2StatusCell';
import useAppNavigation from 'hooks/useAppNavigation';
import { useTenants } from 'TenantModule/hooks/useTenants';
import useSandboxes, { SandboxIsoStateIds } from 'SandboxModule/hooks/useSandboxes';
import { useNotification } from 'hooks/useNotification';
import { useSwitchTenantMutation } from 'TenantModule/api/queries/useSwitchTenant';
import { SandboxStatusTypes } from 'SandboxModule/constants';
import { MEMBER_TABLE_VIEWS } from '@constants';
import { useIntegrationDisplayNamesQuery } from 'IntegrationModule/api/queries/useIntegrationDisplayNamesQuery';
import { ORDER_DIRECTION } from '@constants';
import { ToggleOptionsButton } from 'components/ToggleOptionsButton';
import TeamsV2DeletionTab from 'TeamModule/components/TeamsV2Page/TeamsV2DeletionTab';
import {
  AI_SUGGESTIONS_FILTER_OPTIONS,
  AiSuggestionsFilter,
  aiSuggestionsFilterToPayload,
} from 'TeamModule/components/TeamsV2Page/helpers';
import { csmList } from 'CsmModule/constants';
import { TenantSelector } from 'TenantModule/components/TenantSelector';
import RealmSelector from 'SandboxModule/components/RealmSelector';
import { useTenantDropdown } from 'TenantModule/hooks/useTenantDropdown';
import { useTenantRealmDropdownsFlow } from 'TenantModule/hooks/useTenantRealmDropdownsFlow';
import { PageContainer, MinimalCard, CleanButton, CleanTextField } from 'components/styled';
import { SearchInput } from 'components/SearchInput';
import StandardPageHeader from 'components/StandardPageHeader';

const NUMERIC_ID_REGEX = /^\d+$/;

type FiltersState = {
  tenant: string;
  realmId: string;
  teamIds: string[];
  teamIdsMode: 'include' | 'exclude';
  csm: string;
  tags: string;
  tagsList: string[];
  integration: string;
  text: string;
  ssoEnabled: boolean | undefined;
  openApiEnabled: boolean | undefined;
  ddiEnabled: boolean | undefined;
  internalTestTeam: boolean | undefined;
  chelloCompliant: boolean | undefined;
  aiSuggestions: AiSuggestionsFilter;
  includePersonal: boolean;
  excludeDeactivated: boolean;
  discarded: boolean | undefined;
  deactivationScheduled: boolean | undefined;
  dataDeleted: boolean | undefined;
};

const defaultFilters: FiltersState = {
  tenant: '',
  realmId: '',
  teamIds: [],
  teamIdsMode: 'include',
  csm: '',
  tags: '',
  tagsList: [],
  integration: '',
  text: '',
  ssoEnabled: undefined,
  openApiEnabled: undefined,
  ddiEnabled: undefined,
  internalTestTeam: undefined,
  chelloCompliant: undefined,
  aiSuggestions: 'all',
  includePersonal: false,
  excludeDeactivated: true,
  discarded: undefined,
  deactivationScheduled: undefined,
  dataDeleted: undefined,
};

// ==================== Styled Components ====================

// PageContainer, MinimalCard, CleanButton, CleanTextField now imported from components/styled

const MinimalChip = styled(MuiChip)(({ theme }) => ({
  borderRadius: theme.spacing(1.5), // 6px
  fontWeight: 500,
  fontSize: '0.8125rem',
  height: 24,
  transition: 'all 0.15s ease',
  background: alpha(theme.palette.primary.main, 0.08),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
  '&:hover': {
    background: alpha(theme.palette.primary.main, 0.12),
  },
  '& .MuiChip-deleteIcon': {
    color: theme.palette.primary.main,
    fontSize: '1rem',
    '&:hover': {
      color: theme.palette.primary.dark,
    },
  },
}));

const FilterSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  background: 'transparent',
  borderRadius: theme.spacing(2), // 8px
  border: `1px solid ${theme.palette.mode === 'dark' ? '#1f1f1f' : '#e5e5e5'}`,
}));

const StatsBar = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  padding: theme.spacing(2.5),
  background: 'transparent',
  borderRadius: theme.spacing(2),
  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(148, 163, 184, 0.12)' : 'rgba(226, 232, 240, 0.6)'}`,
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },
}));

const StatItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  '& .stat-value': {
    fontSize: '1.5rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
    lineHeight: 1.3,
  },
  '& .stat-label': {
    fontSize: '0.75rem',
    color: alpha(theme.palette.text.secondary, 0.7),
    fontWeight: 500,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
}));

// ==================== Main Component ====================

export const TeamsV2PageModern = () => {
  const theme = useTheme();
  const { queryParams, pushQueryParams } = useUrl();
  const { pageNumber, handlePageChange, limit } = useTablePagination(50, 1, 1);
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  useEffect(() => {
    if (!queryParams.has('page')) return;
    const mergedParams = Object.fromEntries(queryParams.entries());
    delete (mergedParams as any).page;
    pushQueryParams(mergedParams);
  }, [pushQueryParams, queryParams]);

  const [filters, setFilters] = useState<FiltersState>(defaultFilters);
  const [orderBy, setOrderBy] = useState<string>('lastBackfilled');
  const [orderDirection, setOrderDirection] = useState<
    (typeof ORDER_DIRECTION)[keyof typeof ORDER_DIRECTION]
  >(ORDER_DIRECTION.DESC);
  const [selectedIdsState, setSelectedIdsState] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<'teams' | 'deletion'>('teams');
  const [submittedPayload, setSubmittedPayload] = useState<SearchTeamsByFieldsPayload | null>(null);
  const [teamIdInput, setTeamIdInput] = useState<string>('');
  const [searchInput, setSearchInput] = useState<string>(queryParams.get('q') || '');
  const { navigateToTeamProfile } = useAppNavigation();
  const { showError } = useNotification();
  const { integrationDisplayNames } = useIntegrationDisplayNamesQuery();

  const { tenantOrder, tenantsHash, selectedGlobalTenant, handleSelectGlobalTenant } = useTenants({
    isOff: false,
  });
  const {
    fetchSandboxes,
    getSandboxesByIsoState,
    getSandbox,
    getSelectedRailsSandbox,
    handleSelectGlobalRailsSandbox,
  } = useSandboxes();
  const { switchTenant } = useSwitchTenantMutation();
  const sandboxIsoId = SandboxIsoStateIds.AppbarRealmSelector;

  useEffect(() => {
    fetchSandboxes(sandboxIsoId);
  }, [fetchSandboxes, sandboxIsoId]);

  const buildBaseFromFilters = useCallback(
    (f: FiltersState): Partial<SearchTeamsByFieldsPayload> => {
      // When excludeDeactivated is true, set discarded: false to filter out deactivated teams
      // Otherwise, use the explicit discarded filter value (undefined shows all)
      const discardedValue = f.excludeDeactivated ? false : f.discarded;

      return {
        tenant: f.tenant || undefined,
        realmId: f.realmId || undefined,
        csm: f.csm || undefined,
        ...(f.teamIds && f.teamIds.length > 0
          ? f.teamIdsMode === 'include'
            ? { teamIds: f.teamIds }
            : { teamIdsNot: f.teamIds }
          : {}),
        tags: f.tagsList && f.tagsList.length > 0 ? f.tagsList : undefined,
        integrations: f.integration ? [f.integration] : undefined,
        text: f.text || undefined,
        ssoEnabled: f.ssoEnabled,
        openApiEnabled: f.openApiEnabled,
        ddiEnabled: f.ddiEnabled,
        internalTestTeam: f.internalTestTeam,
        chelloCompliant: f.chelloCompliant,
        ...aiSuggestionsFilterToPayload(f.aiSuggestions),
        personal: f.includePersonal,
        discarded: discardedValue,
        dataDeleted: f.dataDeleted,
      };
    },
    [],
  );

  const buildPayload = useCallback((): SearchTeamsByFieldsPayload => {
    return {
      ...(buildBaseFromFilters(filters) as SearchTeamsByFieldsPayload),
      page: pageNumber,
      limit: limit,
      sortBy: orderBy as any,
      sortOrder: (orderDirection?.toUpperCase?.() as any) || 'DESC',
    };
  }, [buildBaseFromFilters, filters, limit, orderBy, orderDirection, pageNumber]);

  const buildPayloadOverride = useCallback(
    (
      overrides: Partial<{
        page: number;
        sortBy: string;
        sortOrder: (typeof ORDER_DIRECTION)[keyof typeof ORDER_DIRECTION];
      }>,
    ): SearchTeamsByFieldsPayload => {
      return {
        ...buildPayload(),
        ...(overrides.page ? { page: overrides.page } : {}),
        ...(overrides.sortBy ? { sortBy: overrides.sortBy as any } : {}),
        ...(overrides.sortOrder
          ? { sortOrder: (overrides.sortOrder?.toUpperCase?.() as any) || 'DESC' }
          : {}),
      };
    },
    [buildPayload],
  );

  const prevUrlSearchRef = useRef<string | null>(null);

  useEffect(() => {
    const currentUrlSearch = window.location.search;
    const urlChanged = currentUrlSearch !== prevUrlSearchRef.current;
    if (urlChanged) {
      prevUrlSearchRef.current = currentUrlSearch;
    }

    const q = queryParams.get('q') || '';
    const urlTenant = queryParams.get('tenant') || undefined;
    const urlRealmId = queryParams.get('realmId') || undefined;

    const hasUrlFilters = q || urlTenant || urlRealmId;

    if (hasUrlFilters && urlChanged) {
      const nextFilters: FiltersState = {
        ...defaultFilters,
        text: q,
        tenant: urlTenant ?? '',
        realmId: urlRealmId ?? '',
      };

      setFilters(nextFilters);
      setTeamIdInput('');
      setSearchInput(q);
      setSubmittedPayload({
        ...(buildBaseFromFilters(nextFilters) as SearchTeamsByFieldsPayload),
        page: 1,
        limit,
        sortBy: orderBy as any,
        sortOrder: (orderDirection?.toUpperCase?.() as any) || 'DESC',
      });
    } else if (!submittedPayload && !hasUrlFilters) {
      // Only set default payload if there's no URL search param
      setSubmittedPayload(buildPayload());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams]);

  const buildApiParamsFromPayload = useCallback(
    (payload: SearchTeamsByFieldsPayload | null): Record<string, any> => {
      const p = payload || ({} as SearchTeamsByFieldsPayload);
      const {
        tenant,
        realmId,
        teamId,
        csm,
        teamIds,
        teamIdsNot,
        tags,
        integrations,
        ssoEnabled,
        openApiEnabled,
        ddiEnabled,
        internalTestTeam,
        chelloCompliant,
        aiEnabled,
        aiBlocked,
        personal,
        discarded,
        dataDeleted,
        text,
        page = 1,
        limit: payloadLimit = 50,
        sortBy = 'lastBackfilled',
        sortOrder = 'DESC',
      } = p || ({} as SearchTeamsByFieldsPayload);
      const q: Record<string, string | number | boolean | undefined> = {};
      if (tenant) q.tenant = tenant;
      if (realmId) q.realmId = realmId;
      if (teamId) q.teamId = teamId;
      if (csm) q.csm = csm;
      if (teamIds && teamIds.length) q.teamIds = arrayToCsv(teamIds) as any;
      if (teamIdsNot && teamIdsNot.length) q.teamIdsNot = arrayToCsv(teamIdsNot) as any;
      if (tags && tags.length) q.tags = arrayToCsv(tags) as any;
      if (integrations && integrations.length) q.integrations = arrayToCsv(integrations) as any;
      if (typeof ssoEnabled === 'boolean') q.ssoEnabled = booleanToString(ssoEnabled) as any;
      if (typeof openApiEnabled === 'boolean')
        q.openApiEnabled = booleanToString(openApiEnabled) as any;
      if (typeof ddiEnabled === 'boolean') q.ddiEnabled = booleanToString(ddiEnabled) as any;
      if (typeof internalTestTeam === 'boolean')
        q.internalTestTeam = booleanToString(internalTestTeam) as any;
      if (typeof chelloCompliant === 'boolean')
        q.chelloCompliant = booleanToString(chelloCompliant) as any;
      if (typeof aiEnabled === 'boolean') q.aiEnabled = booleanToString(aiEnabled) as any;
      if (typeof aiBlocked === 'boolean') q.aiBlocked = booleanToString(aiBlocked) as any;
      if (typeof personal === 'boolean') q.personal = booleanToString(personal) as any;
      if (typeof discarded === 'boolean') q.discarded = booleanToString(discarded) as any;
      if (typeof dataDeleted === 'boolean') q.dataDeleted = booleanToString(dataDeleted) as any;
      if (text) q.text = text;
      q.page = page;
      q.limit = payloadLimit;
      q.sortBy = sortBy as any;
      q.sortOrder = sortOrder as any;
      return q;
    },
    [],
  );

  const { teamsSearch, isLoading, isFetching, isPlaceholderData, refetch } =
    useSearchTeamsByFieldsQuery(buildApiParamsFromPayload(submittedPayload));
  const teams: TeamSearchItem[] = useMemo(
    () => (teamsSearch?.data && Array.isArray(teamsSearch.data) ? teamsSearch.data : []),
    [teamsSearch],
  );
  // total from API represents total items (not pages)
  const totalTeams = teamsSearch?.total || 0;
  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(totalTeams / (Number(limit) || 1))),
    [totalTeams, limit],
  );

  // Track if auto-navigation has been triggered to prevent navigation loops
  const hasTriggeredAutoNavRef = useRef(false);

  useEffect(() => {
    // Only a result the user's own search brought: never the previous result
    // kept on screen while a new search loads.
    if (isPlaceholderData) return;
    if (totalPages === 1 && teams.length === 1 && !hasTriggeredAutoNavRef.current) {
      const team = teams[0];
      if (!team.dataDeletionExecutedOnDate) {
        hasTriggeredAutoNavRef.current = true;
        // Pass the team's tenant and realmId to ensure the profile page uses the correct context
        navigateToTeamProfile({
          teamId: parseInt(team.teamId, 10),
          tenant: team.tenant,
          realmId: team.realmId,
        });
      }
    }
    // Reset the ref when total changes to a value other than 1
    if (totalPages !== 1) {
      hasTriggeredAutoNavRef.current = false;
    }
  }, [teams, totalPages, navigateToTeamProfile, isPlaceholderData]);

  const allIdsOnPage = useMemo(() => new Set(teams.map((t) => t.id)), [teams]);
  const selectedIds = useMemo(() => new Set(selectedIdsState), [selectedIdsState]);
  const selectedCountOnPage = useMemo(
    () => Array.from(allIdsOnPage).filter((id) => selectedIds.has(id)).length,
    [allIdsOnPage, selectedIds],
  );
  const isAllSelectedOnPage = allIdsOnPage.size > 0 && selectedCountOnPage === allIdsOnPage.size;
  const isIndeterminateOnPage = selectedCountOnPage > 0 && selectedCountOnPage < allIdsOnPage.size;
  const hasSelectionOnPage = selectedCountOnPage > 0;

  const toggleSelectAllOnPage = (checked: boolean) => {
    setSelectedIdsState((prev) => {
      const next = new Set(prev);
      if (checked) {
        allIdsOnPage.forEach((id) => next.add(id));
      } else {
        allIdsOnPage.forEach((id) => next.delete(id));
      }
      return next;
    });
  };

  const toggleRow = (rowKey: string) => {
    setSelectedIdsState((prev) => {
      const next = new Set(prev);
      if (next.has(rowKey)) next.delete(rowKey);
      else next.add(rowKey);
      return next;
    });
  };

  useEffect(() => {
    queueMicrotask(() => {
      setSelectedIdsState(new Set());
    });
  }, [teamsSearch?.data, pageNumber, orderBy, orderDirection, submittedPayload]);

  const onPageChange = useCallback(
    (_event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null, newPage: number) => {
      setSelectedIdsState(new Set());
      handlePageChange({ newPageNumber: newPage });
      setSubmittedPayload(buildPayloadOverride({ page: newPage }));
    },
    [buildPayloadOverride, handlePageChange],
  );
  const paginationProps = useMemo<TablePaginationProps & { indexBase: 1 }>(
    () => ({
      count: totalTeams,
      page: pageNumber,
      rowsPerPage: limit,
      rowsPerPageOptions: [],
      onPageChange,
      indexBase: 1,
    }),
    [limit, onPageChange, pageNumber, totalTeams],
  );
  // Shared Tenant/Realm dropdowns for filters UI
  const { openTenantDropdown } = useTenantDropdown();
  const { onSelectRealm } = useTenantRealmDropdownsFlow();
  const realmDropdownRef = useRef<HTMLDivElement>(null);
  const selectedTenantIdForFilter = useMemo(() => {
    if (!filters.tenant) return null;
    const match = tenantOrder.find((id) => tenantsHash[id]?.tenantName === filters.tenant);
    return match || null;
  }, [filters.tenant, tenantOrder, tenantsHash]);

  const onTenantSelectorClick: React.MouseEventHandler<HTMLElement> = (e) => {
    openTenantDropdown({
      target: e.currentTarget,
      shouldSkipSetTenant: true,
      onSelect: ({ tenant }) => setFilters({ ...filters, tenant: tenant.tenantName, realmId: '' }),
    });
  };
  const onRealmSelectorClick: React.MouseEventHandler<HTMLElement> = (e) => {
    if (!selectedTenantIdForFilter) {
      // No tenant selected, cannot select realm
      return;
    }
    onSelectRealm({
      target: e.currentTarget,
      tenantId: selectedTenantIdForFilter,
      onSelect: ({ realm }) => setFilters({ ...filters, realmId: realm.realm_id }),
    });
  };

  const onClearTenant = () => {
    // Clear both tenant and realmId since realm is tied to tenant
    // (a realm is specific to a tenant, so clearing tenant makes the realm selection invalid)
    setFilters({ ...filters, tenant: '', realmId: '' });
    // Also clear from URL
    const mergedParams = Object.fromEntries(queryParams.entries());
    delete (mergedParams as any).tenant;
    delete (mergedParams as any).realmId;
    pushQueryParams(mergedParams);
  };

  const onClearRealm = () => {
    setFilters({ ...filters, realmId: '' });
    // Also clear from URL
    const mergedParams = Object.fromEntries(queryParams.entries());
    delete (mergedParams as any).realmId;
    pushQueryParams(mergedParams);
  };

  const onSort = useCallback(
    (columnId: string) => {
      if (orderBy === columnId) {
        const nextDir =
          orderDirection === ORDER_DIRECTION.ASC ? ORDER_DIRECTION.DESC : ORDER_DIRECTION.ASC;
        setOrderDirection(nextDir);
        setSelectedIdsState(new Set());
        setSubmittedPayload(buildPayloadOverride({ sortBy: columnId, sortOrder: nextDir }));
      } else {
        const nextDir = ORDER_DIRECTION.DESC;
        setOrderBy(columnId);
        setOrderDirection(nextDir);
        setSelectedIdsState(new Set());
        setSubmittedPayload(buildPayloadOverride({ sortBy: columnId, sortOrder: nextDir }));
      }
    },
    [buildPayloadOverride, orderBy, orderDirection],
  );

  useEffect(() => {
    if (activeTab === 'deletion' && !filters.discarded) {
      queueMicrotask(() => {
        setFilters((prev) => ({ ...prev, discarded: true, excludeDeactivated: false }));
        setSubmittedPayload((prev) => (prev ? { ...prev, discarded: true } : prev));
      });
    }
  }, [activeTab, filters.discarded]);

  useEffect(() => {
    if (activeTab === 'teams' && filters.discarded) {
      queueMicrotask(() => {
        setFilters((prev) => ({ ...prev, discarded: false, excludeDeactivated: true }));
        setSubmittedPayload((prev) => (prev ? { ...prev, discarded: false } : prev));
      });
    }
  }, [activeTab, filters.discarded]);

  useEffect(() => {
    if (activeTab === 'deletion' && (filters.dataDeleted || submittedPayload?.dataDeleted)) {
      queueMicrotask(() => {
        if (filters.dataDeleted) {
          setFilters((prev) => ({ ...prev, dataDeleted: undefined }));
        }
        if (submittedPayload?.dataDeleted) {
          setSubmittedPayload((prev) => (prev ? { ...prev, dataDeleted: undefined } : prev));
        }
      });
    }
  }, [activeTab, filters.dataDeleted, submittedPayload]);

  const handleRowClick = useCallback(
    (team: TeamSearchItem) => {
      if (team.dataDeletionExecutedOnDate) return;
      const idNum = parseInt(team.teamId, 10);
      if (Number.isNaN(idNum)) return;

      const currentRealm = getSelectedRailsSandbox();
      const currentTenantName = selectedGlobalTenant?.tenantName || '';
      const needsTenantRoutes = !!team.tenant && team.tenant !== currentTenantName;
      const needsRealmRoutes =
        !!team.realmId && (!currentRealm || currentRealm.realm_id !== team.realmId);

      // Determine team status to pass to the profile page
      const teamStatus = getTeamStatus(team);

      // Always pass tenant and realmId to ensure URL params are set for breadcrumbs and API calls
      const navParams = {
        teamId: idNum,
        memberTableView: MEMBER_TABLE_VIEWS.ALL_MEMBERS,
        tenant: team.tenant,
        realmId: team.realmId,
        teamStatus,
      };

      if (needsTenantRoutes || needsRealmRoutes) {
        const tenantId = tenantOrder.find((tid) => tenantsHash[tid]?.tenantName === team.tenant);
        const tenantObj = tenantId ? tenantsHash[tenantId] : undefined;
        const realmObj = getSandbox(team.realmId);

        if (realmObj) {
          handleSelectGlobalRailsSandbox({ sandbox: realmObj });
        }

        if (tenantObj) {
          switchTenant(
            { tenant: tenantObj.tenantName },
            {
              onSuccess: () => {
                handleSelectGlobalTenant({ tenant: tenantObj });
                navigateToTeamProfile(navParams);
              },
              onError: () => {
                showError('Failed to switch tenant');
              },
            },
          );
        } else {
          navigateToTeamProfile(navParams);
        }
        return;
      }

      navigateToTeamProfile(navParams);
    },
    [
      getSelectedRailsSandbox,
      selectedGlobalTenant?.tenantName,
      tenantOrder,
      tenantsHash,
      getSandbox,
      handleSelectGlobalRailsSandbox,
      switchTenant,
      handleSelectGlobalTenant,
      navigateToTeamProfile,
      showError,
    ],
  );
  const onSearch = () => {
    handlePageChange({ newPageNumber: 1 });
    setSelectedIdsState(new Set());
    const mergedParams = Object.fromEntries(queryParams.entries());
    const searchText = searchInput.trim();
    if (searchText) mergedParams.q = searchText;
    else delete (mergedParams as any).q;
    delete (mergedParams as any).page;
    pushQueryParams(mergedParams);
    const nextFilters = { ...filters, text: searchText };
    setFilters(nextFilters);
    setSubmittedPayload({
      ...(buildBaseFromFilters(nextFilters) as SearchTeamsByFieldsPayload),
      page: 1,
      limit,
      sortBy: orderBy as any,
      sortOrder: (orderDirection?.toUpperCase?.() as any) || 'DESC',
    });
  };

  const onReset = () => {
    setFilters(defaultFilters);
    setOrderBy('lastBackfilled');
    setOrderDirection(ORDER_DIRECTION.DESC);
    handlePageChange({ newPageNumber: 1 });
    setSelectedIdsState(new Set());
    setTeamIdInput('');
    setSearchInput('');
    const mergedParams = Object.fromEntries(queryParams.entries());
    delete (mergedParams as any).q;
    delete (mergedParams as any).page;
    delete (mergedParams as any).tenant;
    delete (mergedParams as any).realmId;
    pushQueryParams(mergedParams);
    const resetPayload: SearchTeamsByFieldsPayload = {
      ...(buildBaseFromFilters(defaultFilters) as SearchTeamsByFieldsPayload),
      page: 1,
      limit,
      sortBy: 'lastBackfilled' as any,
      sortOrder: ORDER_DIRECTION.DESC as any,
    };
    setSubmittedPayload(resetPayload);
  };

  const handleAddTeamIdChip = useCallback(() => {
    const v = teamIdInput.trim();
    if (!v) return;
    if (!NUMERIC_ID_REGEX.test(v)) return;
    setFilters((prev) => ({ ...prev, teamIds: Array.from(new Set([...(prev.teamIds || []), v])) }));
    setTeamIdInput('');
  }, [teamIdInput]);

  const removeTeamIdChip = (id: string) =>
    setFilters((prev) => ({ ...prev, teamIds: (prev.teamIds || []).filter((x) => x !== id) }));

  const filterOptions: Array<{ key: keyof FiltersState; label: string }> = [
    { key: 'excludeDeactivated', label: 'Exclude Deactivated' },
    { key: 'ssoEnabled', label: 'SSO Enabled' },
    { key: 'openApiEnabled', label: 'Open API' },
    { key: 'ddiEnabled', label: 'DDI Enabled' },
    { key: 'internalTestTeam', label: 'Test Teams' },
    { key: 'chelloCompliant', label: 'Chello Compliant' },
    { key: 'dataDeleted', label: 'Deleted' },
    { key: 'includePersonal', label: 'Personal' },
  ];

  const handleFilterCheckboxChange = useCallback(
    (key: keyof FiltersState) => (_e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      setFilters(
        (prev) =>
          ({
            ...prev,
            [key]: key === 'dataDeleted' ? (checked ? true : undefined) : checked,
          }) as FiltersState,
      );
    },
    [],
  );

  useEffect(() => {
    if (!filters.tenant) return;
    if (filters.realmId === '') {
      return;
    }
    if (filters.realmId) {
      const currentRealm = getSandbox(filters.realmId);
      if (
        currentRealm &&
        currentRealm.tenant === filters.tenant &&
        currentRealm.status !== SandboxStatusTypes.Archived &&
        currentRealm.is_ready
      ) {
        return;
      }
    }

    const globalRealm = getSelectedRailsSandbox();
    // Guard: only call setState if all conditions are met AND value is different
    if (
      globalRealm &&
      globalRealm.tenant === filters.tenant &&
      globalRealm.status !== SandboxStatusTypes.Archived &&
      globalRealm.is_ready &&
      filters.realmId !== globalRealm.realm_id
    ) {
      setFilters((prev) => ({ ...prev, realmId: globalRealm.realm_id }));
      return;
    }

    // Early return if realm matches (no setState needed)
    if (
      globalRealm &&
      globalRealm.tenant === filters.tenant &&
      globalRealm.status !== SandboxStatusTypes.Archived &&
      globalRealm.is_ready
    ) {
      return;
    }

    const tenantId = tenantOrder.find((tid) => tenantsHash[tid]?.tenantName === filters.tenant);
    const tenantObj = tenantId ? tenantsHash[tenantId] : undefined;
    const candidateRealmIds: string[] = Array.from(
      new Set(
        [tenantObj?.primaryRealm, ...(tenantObj?.realms || [])].filter((x): x is string => !!x),
      ),
    );

    const nextRealmId = candidateRealmIds.find((rid) => {
      const sb = getSandbox(rid);
      return (
        !!sb &&
        sb.tenant === filters.tenant &&
        sb.status !== SandboxStatusTypes.Archived &&
        sb.is_ready
      );
    });

    const desiredRealmId = nextRealmId || '';
    if (filters.realmId !== desiredRealmId) {
      setFilters((prev) => ({ ...prev, realmId: desiredRealmId }));
    }
  }, [
    filters.tenant,
    filters.realmId,
    getSandbox,
    getSelectedRailsSandbox,
    tenantOrder,
    tenantsHash,
  ]);

  // Active filter count
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.tenant) count++;
    if (filters.realmId) count++;
    if (filters.csm) count++;
    if (filters.integration) count++;
    if (filters.teamIds.length > 0) count++;
    if (filters.tagsList.length > 0) count++;
    if (filters.text) count++;
    if (filters.ssoEnabled !== undefined) count++;
    if (filters.openApiEnabled !== undefined) count++;
    if (filters.ddiEnabled !== undefined) count++;
    if (filters.internalTestTeam !== undefined) count++;
    if (filters.chelloCompliant !== undefined) count++;
    if (filters.aiSuggestions !== 'all') count++;
    if (filters.dataDeleted !== undefined) count++;
    if (filters.includePersonal) count++;
    return count;
  }, [filters]);

  return (
    <PageContainer>
      <Helmet title="Teams Management" />
      {/* Header Section with Breadcrumbs */}
      <StandardPageHeader
        title="Teams Management"
        subtitle="Search, filter, and manage all teams across your organization"
        icon="Teams Management"
        sx={{ mb: 6 }}
      />
      {/* Stats Bar */}
      <Grow in timeout={600}>
        <StatsBar sx={{ mb: 4 }}>
          <StatItem>
            <Users size={24} color={theme.palette.primary.main} />
            <Box>
              <Typography className="stat-value">{totalTeams.toLocaleString()}</Typography>
              <Typography className="stat-label">Total Teams</Typography>
            </Box>
          </StatItem>
          {selectedCountOnPage > 0 && (
            <StatItem>
              <Box>
                <Typography className="stat-value">{selectedCountOnPage}</Typography>
                <Typography className="stat-label">Selected</Typography>
              </Box>
            </StatItem>
          )}
          <StatItem sx={{ ml: 'auto' }}>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body2" sx={{ color: alpha(theme.palette.text.secondary, 0.7) }}>
                {isFetching ? 'Updating...' : 'Up to date'}
              </Typography>
              {isFetching && (
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                >
                  <RefreshCw size={16} color={theme.palette.primary.main} />
                </motion.div>
              )}
            </Box>
          </StatItem>
        </StatsBar>
      </Grow>{' '}
      {/* Search and Quick Actions */}
      <MinimalCard $hoverEffect={false} mb={3}>
        <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
          <SearchInput
            fullWidth
            placeholder="Search by name, team ID, target service ID, UUID…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') onSearch();
            }}
            InputProps={{
              endAdornment: searchInput && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchInput('')}>
                    <X size={16} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1, minWidth: 300 }}
          />

          <CleanButton
            variant="contained"
            startIcon={<Search size={18} />}
            onClick={onSearch}
            disabled={isFetching}
          >
            Search
          </CleanButton>

          <CleanButton
            variant="outlined"
            startIcon={filtersExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
            endIcon={
              activeFilterCount > 0 && (
                <Box
                  sx={{
                    background: theme.palette.primary.main,
                    color: 'white',
                    borderRadius: '50%',
                    width: 20,
                    height: 20,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.75rem',
                    fontWeight: 700,
                  }}
                >
                  {activeFilterCount}
                </Box>
              )
            }
            onClick={() => setFiltersExpanded(!filtersExpanded)}
          >
            Filters
          </CleanButton>

          <CleanButton variant="outlined" startIcon={<RefreshCw size={18} />} onClick={onReset}>
            Reset
          </CleanButton>
        </Box>
      </MinimalCard>
      {/* Expandable Filters */}
      <Collapse in={filtersExpanded}>
        <MinimalCard $hoverEffect={false} mb={3}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Box display="flex" alignItems="center" gap={1}>
              <Filter size={20} color={theme.palette.primary.main} />
              <Typography variant="h6" fontWeight={600}>
                Advanced Filters
              </Typography>
            </Box>
            {activeFilterCount > 0 && (
              <CleanButton
                size="small"
                variant="text"
                startIcon={<X size={16} />}
                onClick={onReset}
              >
                Clear All
              </CleanButton>
            )}
          </Box>

          <FilterSection mb={3}>
            <Typography
              variant="subtitle2"
              sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <Building2 size={16} />
              Organization
            </Typography>
            <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }} gap={2}>
              <TenantSelector
                onClick={onTenantSelectorClick}
                tenantId={selectedTenantIdForFilter}
                onClear={onClearTenant}
              />
              <RealmSelector
                onClick={onRealmSelectorClick}
                realmId={filters.realmId || ''}
                ref={realmDropdownRef}
                onClear={onClearRealm}
              />
              <FormControl fullWidth variant="outlined">
                <InputLabel id="integration-select-label">Integration</InputLabel>
                <Select
                  labelId="integration-select-label"
                  label="Integration"
                  value={filters.integration}
                  onChange={(e) =>
                    setFilters({ ...filters, integration: String(e.target.value || '') })
                  }
                >
                  <MenuItem value="">All</MenuItem>
                  {integrationDisplayNames.map((integration) => (
                    <MenuItem key={integration.nonDisplayName} value={integration.nonDisplayName}>
                      {integration.displayName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth variant="outlined">
                <InputLabel id="ai-suggestions-select-label">AI Suggestions</InputLabel>
                <Select
                  labelId="ai-suggestions-select-label"
                  label="AI Suggestions"
                  value={filters.aiSuggestions}
                  onChange={(e) =>
                    setFilters({
                      ...filters,
                      aiSuggestions: e.target.value as AiSuggestionsFilter,
                    })
                  }
                  data-testid="teams-v2-ai-suggestions-filter"
                >
                  {AI_SUGGESTIONS_FILTER_OPTIONS.map(({ value, label }) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth variant="outlined">
                <InputLabel id="csm-select-label">CSM</InputLabel>
                <Select
                  labelId="csm-select-label"
                  label="CSM"
                  value={filters.csm}
                  onChange={(e) => setFilters({ ...filters, csm: String(e.target.value || '') })}
                >
                  <MenuItem value="">All</MenuItem>
                  {csmList.map((csm) => (
                    <MenuItem key={csm.accountId} value={csm.name}>
                      {csm.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </FilterSection>

          <FilterSection mb={3}>
            <Typography
              variant="subtitle2"
              sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <Tag size={16} />
              Team IDs & Tags
            </Typography>
            <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }} gap={2}>
              <Box>
                <Box display="flex" alignItems="center" gap={2} mb={1}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Team ID"
                    placeholder="Enter ID and press Enter"
                    value={teamIdInput}
                    onChange={(e) => setTeamIdInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleAddTeamIdChip();
                    }}
                  />
                  <ButtonGroup variant="outlined" size="small">
                    <Button
                      variant={filters.teamIdsMode === 'include' ? 'contained' : 'outlined'}
                      onClick={() => setFilters({ ...filters, teamIdsMode: 'include' })}
                    >
                      Include
                    </Button>
                    <Button
                      variant={filters.teamIdsMode === 'exclude' ? 'contained' : 'outlined'}
                      onClick={() => setFilters({ ...filters, teamIdsMode: 'exclude' })}
                    >
                      Exclude
                    </Button>
                  </ButtonGroup>
                </Box>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {(filters.teamIds || []).map((id) => (
                    <MinimalChip key={id} label={id} onDelete={() => removeTeamIdChip(id)} />
                  ))}
                </Box>
              </Box>

              <Box>
                <TeamTagsAutocomplete
                  value={filters.tagsList || []}
                  onChange={(next) => setFilters((prev) => ({ ...prev, tagsList: next }))}
                  label="Tags"
                  placeholder="Select or type a tag"
                />
              </Box>
            </Box>
          </FilterSection>

          <FilterSection>
            <Typography
              variant="subtitle2"
              sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <Settings size={16} />
              Feature Flags
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={2}>
              {filterOptions.map(({ key, label }) => {
                const isDeletedFilter = key === 'dataDeleted';
                const isDisabled = isDeletedFilter && activeTab === 'deletion';
                const isChecked = isDisabled ? false : !!filters[key];

                return (
                  <FormControlLabel
                    key={String(key)}
                    control={
                      <Checkbox
                        checked={isChecked}
                        onChange={handleFilterCheckboxChange(key)}
                        disabled={isDisabled}
                      />
                    }
                    label={label}
                  />
                );
              })}
            </Box>
          </FilterSection>
        </MinimalCard>
      </Collapse>
      {/* Tab Navigation */}
      <MinimalCard $hoverEffect={false} mb={3}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={2}
        >
          <TeamsV2Tabs currentTab={activeTab} onChangeTab={setActiveTab} />
        </Box>
      </MinimalCard>
      {/* Table Content */}
      <MinimalCard $hoverEffect={false}>
        {activeTab === 'teams' && (
          <TeamsV2Table
            teams={teams}
            isLoading={isLoading}
            selectedIds={selectedIds}
            hasSelectionOnPage={hasSelectionOnPage}
            allOnPageSelected={isAllSelectedOnPage}
            isIndeterminateOnPage={isIndeterminateOnPage}
            onToggleRow={toggleRow}
            onToggleSelectAll={toggleSelectAllOnPage}
            paginationProps={paginationProps}
            orderBy={orderBy}
            orderDirection={orderDirection}
            onSort={onSort}
            onRowClick={handleRowClick}
            onAfterDeactivate={() => refetch()}
            filtersPayload={submittedPayload || buildPayload()}
            exportFileName={'teams'}
          />
        )}

        {activeTab === 'deletion' && (
          <TeamsV2DeletionTab filtersPayload={submittedPayload || buildPayload()} />
        )}
      </MinimalCard>
    </PageContainer>
  );
};

export default TeamsV2PageModern;

/* ---------------------------------- Tabs --------------------------------- */
type TeamsV2TabValue = 'teams' | 'deletion';

type TeamsV2TabsProps = {
  currentTab: TeamsV2TabValue;
  onChangeTab: (tab: TeamsV2TabValue) => void;
};

const TeamsV2Tabs = ({ currentTab, onChangeTab }: TeamsV2TabsProps) => {
  const options: ComponentProps<typeof ToggleOptionsButton>['options'] = [
    { label: 'Active Teams', value: 'teams' },
    { label: 'Scheduled for Deletion', value: 'deletion' },
  ];

  const onSelect: ComponentProps<typeof ToggleOptionsButton>['onSelect'] = ({ option }) => {
    onChangeTab(option.value as TeamsV2TabValue);
  };

  return <ToggleOptionsButton options={options} currentOption={currentTab} onSelect={onSelect} />;
};
